/**
 * Layout: Theme
 * -----------------------------------------------------------------------------
 * Entry file for all global theme scripts and registering sections and modules.
 *
 */
import '@/styles/theme.scss';

import { focusHash, bindInPageLinks } from '@shopify/theme-a11y';
import { load } from '@shopify/theme-sections';
import axios from 'axios';
import Toggle from '~/components/toggle';
import policyModal from '../components/policy-modal';
import Cookies from 'js-cookie';

/**
 * Global theme helpers.
 */
import { keyboardTabbable } from '~/helpers/a11y';
import AjaxCart from '~/helpers/ajax-cart';
import breakpoints from '../helpers/breakpoints';
import Currency from '~/helpers/currency';
import cssClasses from '~/helpers/cssClasses';
import EventBus from '~/helpers/event-bus';
import FadeInElements from '~/helpers/fade-in-elements';
import Redirects from '~/helpers/redirects';
import { getBrowser, on } from '~/helpers/utils';
import ScrollDirection from '~/mixins/scroll-direction';
import SmoothScroll from '~/helpers/smooth-scroll';
import DataLayer from '~/helpers/datalayers';

import '~/components/filter.js'
import '~/components/quick-buy-modal.js'
import '../../styles/components/skeleton-card';
import '../../styles/components/loading-overlay';

/**
 * Global theme section imports.
 */
import '~/sections/cart-drawer';
import '~/sections/menu-drawer';
import '~/sections/site-header';
import '~/sections/site-footer';

/**
 * Global theme component imports.
 */
// import FreeGift from '~/components/free-gift';
import engravingLineItems from '~/components/engraving-line-items';
import upsellPromo from '~/components/upsell-promo';
import FreeShippingNotification from '~/components/free-shipping-notification';
import RedirectModal from '~/components/redirect-modal';
import SearchBar from '~/components/search-bar';
import TopBar from '~/components/top-bar';
import Gorgias from '~/components/gorgias';
import Accordion from '~/components/accordion';

import video from '~/components/video';

import SwatchListing from '~/components/swatch-listing';
import Gwp from '~/components/gwp';

/* import '~/sections/newsletter-modal'; */

/**
 * Global Frame utils.
 */
window.Frame = window.Frame || {};
window.Frame.EventBus = EventBus();

Cookies.set('checkout_reloaded', '', { expires: 1 });

document.addEventListener('DOMContentLoaded', () => {
  if (getBrowser() === 'Internet Explorer') {
    axios.defaults.headers.common = {

      /**
       * IE11 caching bug.
       */
      Pragma: 'no-cache',
    };
  }

  if (theme.pageType !== 'product') {
    localStorage.removeItem('datalayerList');
  }

  /**
   * DOM selectors.
   */
  const selectors = {
    siteHeaderWrapper: '[js-site-header="wrapper"]',
    siteHeader: '[js-site-header="container"]',
    accordion: '.accordion__standard',
    collectionDesktopFilters: '.template-collection__tools-container',
  };

  /**
   * Instance globals.
   */
  let AnimatedHeader = {};

  /**
   * DOM node selectors.
   */
  const nodeSelectors = {
    siteHeaderWrapper: document.querySelector(selectors.siteHeaderWrapper),
    siteHeader: document.querySelector(selectors.siteHeader),
    accordion: document.querySelector(selectors.accordion),
    collectionDesktopFilters: document.querySelector(selectors.collectionDesktopFilters),
  };

  /**
   * Instantiate a collapsed header on scroll using scrollDirection helper.
   */
  function constructStickyHeader() {
    AnimatedHeader = ScrollDirection({
      throttle: 250,
      start: 100,
    });
  }

  /**
   * Set sticky header events after document has loaded styling.
   */
  function setStickyHeader() {
    on('load', () => {
      AnimatedHeader.init();
      nodeSelectors.siteHeaderWrapper.classList.add(cssClasses.sticky);
    });
  }

  load('*');

  DataLayer().init();

  Redirects().init();
  RedirectModal().init();

  setStickyHeader();
  constructStickyHeader();

  AjaxCart().init();
  SearchBar().init();
  keyboardTabbable().init();
  SmoothScroll().init();
  FadeInElements().init();

  if (theme.features.currencyConverter) {
    Currency().init();
  }

  if (theme.features.freeGift) {
    FreeGift().init();
  }
  if (theme.engraving.allow === true) {
    engravingLineItems().init();
  }

  if (theme.features.upsellPromo) {
    upsellPromo().init();
  }

  if (theme.features.freeShippingNotification) {
    FreeShippingNotification().init();
  }

  if (theme.gwp24) {
    Gwp().init();
  }
  /*
    if(nodeSelectors.collectionDesktopFilters !== null) {
      StickyFilters().init();
    }
  */

  TopBar().init();
  Gorgias().init();
  focusHash();
  bindInPageLinks();
  if (nodeSelectors.accordion !== null) {
    Accordion('.accordion__standard', {
      singleOpen: true,
      expandInitial: false,
    }).init();
  }

  if (theme.pageType == 'collection' || theme.pageType == 'search') {
    const Drawer = Toggle({
      toggleSelector: 'filterDrawer',
      scrollLock: true,
    });
    Drawer.init();
  }
  video().init();

  SwatchListing().init();
  window.addEventListener('swatchesUpdate:needed', function () {
    SwatchListing().init();
  });

  policyModal().init();

  /**'ajaxinate:rendered'
   * Global Breakpoint Change Listener
   * - Listens for changes to the match media to detect if the breakpoint has
   *   changed.
   * - Emits an EventBus event so that components can listen for breakpoint
   *   changes.
   */
  const mediaQueryList = window.matchMedia(`(min-width: ${breakpoints.large})`);

  on('change', mediaQueryList, (event) => {
    const displayType = event.matches ? 'desktop' : 'mobile';
    Frame.EventBus.emit('Resize:BreakpointChange', displayType);
  });
});
